import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { MdMenu, MdLogout } from '../../utils/icons';
import './Header.css';

interface HeaderProps {
  title: string;
  onMenuClick: () => void;
}

interface IdTokenClaims {
  roles?: string[];
  login_hint?: string;
}

const TIER_PRECEDENCE = ['Admin', 'Tester', 'Developer'] as const;
const ROLE_TITLES: Record<string, string> = {
  Admin: 'Admin',
  Tester: 'Tester',
  Developer: 'Developer',
};

const Header: React.FC<HeaderProps> = ({ title, onMenuClick }) => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = () => {
    const claims = account?.idTokenClaims as IdTokenClaims | undefined;
    instance.logoutRedirect({
      account,
      logoutHint: claims?.login_hint ?? account?.username,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const initials = account?.name
    ? account.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const roles = (account?.idTokenClaims as IdTokenClaims)?.roles ?? [];
  const primaryRole = TIER_PRECEDENCE.find((r) => roles.includes(r));

  return (
    <header className="dash-header">
      <div className="dash-header-left">
        <button className="btn-hamburger" onClick={onMenuClick} aria-label="Toggle menu">
          <MdMenu aria-hidden="true" />
        </button>
        <h1 className="dash-header-title">{title}</h1>
      </div>

      <div className="dash-header-right">
        <div className="dash-user">
          <div className="dash-avatar">{initials}</div>
          <div className="dash-user-info">
            <span className="dash-user-name">{account?.name ?? 'User'}</span>
            <span className="dash-user-email">{account?.username}</span>
            {primaryRole && (
              <div className="dash-user-roles">
                <span className="dash-role-badge" title={ROLE_TITLES[primaryRole]}>
                  {primaryRole}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="logout-wrap">
          <button
            className={`btn-icon-header btn-logout-header${showLogoutConfirm ? ' btn-logout-header--active' : ''}`}
            onClick={() => setShowLogoutConfirm(v => !v)}
            aria-label="Sign out"
            aria-expanded={showLogoutConfirm}
          >
            <MdLogout aria-hidden="true" />
          </button>

          {showLogoutConfirm && (
            <div className="logout-popover" role="dialog" aria-modal="true" aria-labelledby="logout-title">
              <div className="logout-popover-arrow" />
              <div className="logout-popover-header">
                <div className="logout-popover-icon">
                  <MdLogout aria-hidden="true" />
                </div>
                <div>
                  <p className="logout-popover-title" id="logout-title">Sign out</p>
                  <p className="logout-popover-desc">Are you sure you want to sign out of OmniLinQ?</p>
                </div>
              </div>
              <div className="logout-popover-actions">
                <button className="logout-btn-cancel" onClick={() => setShowLogoutConfirm(false)}>
                  Cancel
                </button>
                <button className="logout-btn-confirm" onClick={handleLogout}>
                  <MdLogout aria-hidden="true" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
